import { useMemo } from "react";

import { useClients } from "@/hooks/use-clients";
import type { Database } from "@shared/database.types";

type Client = Database["public"]["Tables"]["clients"]["Row"];

/** Oro della casa: l'accento di riserva quando il cliente non ha un brand. */
const GOLD = "hsl(var(--primary))";

export interface PortalClientState {
  /** Il cliente del portale: la RLS ne lascia passare uno solo. */
  client: Client | null;
  /**
   * Colore-dato da passare alle primitive come `accentColor` (§5).
   * Le primitive lo smorzano da sole (fill 12-15%, bordo 30%): non usarlo
   * mai come colore di testo, il contrasto non è garantito.
   */
  accent: string;
  /** true se l'accento arriva davvero dal brand del cliente. */
  hasBrand: boolean;
  /** Nome da mostrare: mai un id, mai una key. */
  name: string | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Identità del cliente loggato nel portale. Fonte unica: nessuna pagina
 * deve ripescare `clients[0]` a mano.
 */
export function usePortalClient(): PortalClientState {
  const { data, isLoading, isError } = useClients();

  return useMemo(() => {
    const client = (data ?? [])[0] ?? null;
    const brand = client?.brand_color?.trim();
    const hasBrand = !!brand;
    return {
      client,
      accent: hasBrand ? brand! : GOLD,
      hasBrand,
      name: client?.name ?? null,
      isLoading,
      isError,
    };
  }, [data, isLoading, isError]);
}
